import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, Button, IconButton, useTheme, Fade } from "@mui/material";
import { Close as CloseIcon, GetApp as GetAppIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const AppDownloadOverlay = ({ position = "bottom" }) => {
  const STORAGE_KEY = "app_overlay_last_dismissed";
  const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const lastDismissed = localStorage.getItem(STORAGE_KEY);
  
    if (!lastDismissed) {
      // Never dismissed → show
      setIsVisible(true);
      return;
    }
  
    const now = Date.now();
    const dismissedAt = Number(lastDismissed);
  
    if (now - dismissedAt >= ONE_WEEK_MS) {
      setIsVisible(true);
    }
  }, []);
  

  const handleClose = (e) => {
    e.stopPropagation();
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  const handleDownload = () => {
    const landingUrl = process.env.REACT_APP_APK_LANDING_URL;
    if (landingUrl) {
      window.open(landingUrl, "_blank");
    } else {
      window.location.href = `${window.location.origin}/${process.env.REACT_APP_APK}`;
    }
  };

  if (!isVisible) return null;

  return ReactDOM.createPortal(
    <Fade in={isVisible}>
      {/* 1. Backdrop: Darkens the rest of the screen */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.7)", // Darkness level
          zIndex: 2147483647, // Higher than livechat
          display: "flex",
          flexDirection: "column",
          justifyContent: position === "top" ? "flex-start" : "flex-end",
          pointerEvents: "auto", 
        }}
        onClick={handleClose} // Clicking the dark area also closes it
      >
        {/* 2. The Bar: Glassmorphism Style */}
        <Box
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking the bar itself
          sx={{
            width: "100%",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            
            // Glassmorphism properties
            background: "rgba(255, 255, 255, 0.1)", 
            backdropFilter: "blur(15px) saturate(150%)",
            WebkitBackdropFilter: "blur(15px) saturate(150%)",
            borderTop: position === "bottom" ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
            borderBottom: position === "top" ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
            boxShadow: "0 -4px 30px rgba(0, 0, 0, 0.5)",
            
            // Absolute positioning disregard of navs
            position: "relative",
            bottom: position === "bottom" ? 0 : "auto",
            top: position === "top" ? 0 : "auto",
            paddingBottom: position === "bottom" ? `calc(12px + env(safe-area-inset-bottom, 12px))` : "12px",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ 
                color: "rgba(255, 255, 255, 0.8)", 
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            
            <img 
              src={process.env.REACT_APP_HOME_APP_DOWNLOAD} 
              alt="App Logo" 
              style={{ 
                width: "40px", 
                height: "40px", 
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.2)" 
              }} 
            />
            
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1.2 }}>
                {t("appdownloadoverlay.title", { defaultValue: "Official App" })} 
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "11px" }}>
                {t("appdownloadoverlay.desc", { defaultValue: "Faster, Safer, Better." })} 
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleDownload}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: `0 0 15px ${theme.palette.primary.main}66`,
              "&:hover": { backgroundColor: theme.palette.primary.dark }
            }}
          >
            {t("appdownloadoverlay.button", { defaultValue: "Install" })} 
          </Button>
        </Box>
      </Box>
    </Fade>,
  document.body 
);
};

export default AppDownloadOverlay;